/**
 * Collection Packages API service
 *
 * Provides functions for building, monitoring, and downloading
 * Velociraptor offline collector packages.
 */

import { apiClient } from './api'
import { API_BASE_URL } from './api-base-url'
import type { CollectionRequest } from '../types/collection-requests'

// =============================================================================
// PACKAGE BUILD TYPES
// =============================================================================

/**
 * Status of a package build
 */
export interface PackageBuildStatus {
  requestId: string
  status: string
  packageHash: string | null
  packageSizeBytes: number | null
  packageGeneratedAt: string | null
}

// =============================================================================
// PACKAGE API
// =============================================================================

/**
 * Trigger package generation for a collection request.
 *
 * @param collectorOnly - When true, builds only the Velociraptor collector
 *   binary without the full EvidenX package (schedule, instructions, CoC).
 */
export async function buildPackage(
  caseId: string,
  requestId: string,
  collectorOnly = false
): Promise<CollectionRequest> {
  const query = collectorOnly ? '?collectorOnly=true' : ''
  return apiClient.post<CollectionRequest>(
    `/cases/${caseId}/collection-requests/${requestId}/generate${query}`
  )
}

/**
 * Get the current package/build status for a collection request
 */
export async function getPackageStatus(
  caseId: string,
  requestId: string
): Promise<PackageBuildStatus> {
  const request = await apiClient.get<CollectionRequest>(
    `/cases/${caseId}/collection-requests/${requestId}`
  )

  return {
    requestId: request.id,
    status: request.packageStatus,
    packageHash: request.packageHash,
    packageSizeBytes: request.packageSizeBytes,
    packageGeneratedAt: request.packageGeneratedAt,
  }
}

/**
 * Download the assembled package ZIP from the backend.
 *
 * The backend serves the complete ZIP (collector binary + supporting documents)
 * directly — no client-side bundling needed.
 */
export async function downloadPackage(caseId: string, requestId: string): Promise<void> {
  const token = localStorage.getItem('evidenx_access_token') ?? ''
  const response = await fetch(
    `${API_BASE_URL}/cases/${caseId}/collection-requests/${requestId}/package/download`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )

  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText)
    throw new Error(`Failed to download package: ${text}`)
  }

  const blob = await response.blob()
  const disposition = response.headers.get('content-disposition')
  const filename =
    disposition?.match(/filename="(.+)"/)?.[1] ?? `collector-package-${requestId}.zip`

  // Trigger browser download
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}
